/* eslint-disable brace-style */
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';

import { Observable, of, ReplaySubject } from 'rxjs';
import { startWith, filter, takeUntil } from 'rxjs/operators';
import { Admission, CropAid, ProductTypeEnum } from 'src/app/crop-aid/crop-aid.model';
import { selectEditingTemplate } from 'src/app/template/store/selectors';

import {
  Treatment,
  SalesOrderPeriod,
  AidAdvice,
  SiteState,
  TemplateType,
  Template,
  Objective,
} from '../../models';

export interface TreatmentDialogData extends AbstractFormDialogData {
  treatment: Treatment;
  cropAids: CropAid[];
  salesOrderPeriods: SalesOrderPeriod[];
  template: Template;
  objectives: Objective[];
  cropId: number;
  isPlanetProof: boolean;
}

@Component({
  selector: 'app-treatment-dialog',
  templateUrl: './treatment-dialog.component.html',
  styleUrls: ['treatment-dialog.component.scss'],
  standalone: false,
})
export class TreatmentDialogComponent
  extends AbstractFormDialog<Treatment>
  implements OnInit, OnDestroy
{
  data = inject<TreatmentDialogData>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
    inject<MatDialogRef<TreatmentDialogComponent, AbstractDialogResultData<Treatment>>>(
      MatDialogRef,
    );
  private readonly locale = inject(LocaleService);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  selectedProduct: CropAid;
  driftReductions = [75, 90, 95, 97.5, 99];
  aidAdvicesFormArray = this.formBuilder.array(
    this.data.treatment.aidAdvices.map((aidAdvice) => this.buildAidAdviceFormGroup(aidAdvice)),
  );
  aidAdviceFormGroup: FormGroup = this.newAidAdviceFormGroup();
  filteredProducts$: Observable<CropAid[]>;
  formGroup = this.formBuilder.group({
    id: [this.data.treatment.id],
    moment: [this.data.treatment.moment, [Validators.maxLength(4000)]],
    pointOfAttention: [this.data.treatment.pointOfAttention, [Validators.maxLength(4000)]],
    frequency: [
      this.data.treatment.frequency,
      [Validators.required, Validators.min(1), Validators.max(20)],
    ],
    name: [this.data.treatment.name, [Validators.required, Validators.minLength(1)]],
    objective: [
      this.data.treatment.objective,
      [Validators.required, Validators.minLength(1), Validators.maxLength(100)],
    ],
    periodId: [this.data.treatment.periodId, [Validators.required]],
    driftReduction: [
      this.data.treatment.driftReduction || this.getDefaultDriftReduction(),
      this.hasTemplateTypeGbm() ? [Validators.required] : [],
    ],
    aidAdvices: this.aidAdvicesFormArray,
  });
  templateType: TemplateType;
  addingProduct = false;
  editedProduct: number;
  cropId = this.data.cropId;
  cropAidAdvices: CropAid[];

  ngOnInit() {
    this.cropAidAdvices = this.data.treatment.aidAdvices.map((a) => ({
      ...this.findProduct(a.cropAidId),
      ...a,
    }));

    this.store.pipe(select(selectEditingTemplate)).subscribe((editingTemplate) => {
      if (editingTemplate && editingTemplate.templateType) {
        this.templateType = editingTemplate.templateType;
      }
    });

    if (this.data.treatment.aidAdvices.length === 0) {
      this.addProduct();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  selectProduct(cropAidId: number, updateSelectedProduct = false) {
    if (updateSelectedProduct) {
      this.selectedProduct = this.findProduct(cropAidId);
    }

    if (!this.formGroup.controls.name.value) {
      this.formGroup.controls.name.setValue(
        this.data.cropAids.find(({ id }) => cropAidId === id)?.name,
      );
    }
  }

  getCropAid(id: number) {
    const cropAidFound = this.data.cropAids.find((cropAid) => cropAid.id === id);
    if (cropAidFound) {
      return cropAidFound;
    }
    return {
      id: 0,
      indicativePrice: 0,
      isActive: false,
      isCropProtection: false,
      isFertilizer: false,
      name: '-',
      productType: null,
      unitOfMeasurement: null,
      forbiddenForPlanetProof: false,
    };
  }

  getSortedCropAids() {
    return this.data.cropAids.sort((a, b) =>
      a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1,
    );
  }

  removeProduct(index: number) {
    this.cropAidAdvices.splice(index, 1);
    this.aidAdvicesFormArray.removeAt(index);

    if (this.aidAdvicesFormArray.length === 0) {
      this.addProduct();
    }
  }

  editProduct(index: number) {
    this.editedProduct = index;
  }

  doneEditingProduct() {
    if (this.editedProduct != null && this.editedProduct >= 0) {
      const aidAdvice = this.aidAdvicesFormArray.controls[this.editedProduct].value as AidAdvice;
      const cropAidAdvice = { ...this.findProduct(aidAdvice.cropAidId), ...aidAdvice };
      this.cropAidAdvices[this.editedProduct] = cropAidAdvice;
    }
    this.editedProduct = null;
    this.filteredProducts$ = of(this.getSortedCropAids());
  }

  addProduct() {
    this.aidAdvicesFormArray.push(this.aidAdviceFormGroup);
    this.addingProduct = true;
  }

  checkProduct() {
    if (!this.selectedProduct) {
      this.aidAdviceFormGroup.get('cropAidId').setValue(null);
    }
  }

  displayProduct() {
    return (cropAidId?: number) => (cropAidId ? this.findProduct(cropAidId)?.name : '');
  }

  doneAddingProduct() {
    const dosageField = this.aidAdviceFormGroup.get('dosage');
    const validatedDosage = this.stripOtherThanNumbers(String(dosageField.value));
    dosageField.setValue(validatedDosage);

    if (this.aidAdviceFormGroup.valid && this.selectedProduct) {
      const result = this.aidAdviceFormGroup.value as AidAdvice;
      const cropAidAdvice = { ...this.findProduct(result.cropAidId), ...result };
      this.cropAidAdvices.push(cropAidAdvice);
      this.addingProduct = false;
      this.aidAdviceFormGroup = this.newAidAdviceFormGroup();
      this.filteredProducts$ = of(this.getSortedCropAids());
    }
  }

  cancelAddingProduct() {
    this.addingProduct = false;
    this.aidAdviceFormGroup.reset();
    this.aidAdvicesFormArray.removeAt(this.aidAdvicesFormArray.length - 1);
    this.filteredProducts$ = of(this.getSortedCropAids());
  }

  isGbmCropAid(cropAid: CropAid) {
    return this.hasTemplateTypeGbm() ? cropAid.isCropProtection : cropAid.isFertilizer;
  }

  hasTemplateTypeGbm() {
    return ProductTypeEnum.GBM.toString() === this.data.template.templateType;
  }

  getAdmissionNumberString(admissions: Admission[]) {
    if (!admissions || admissions.length < 1) {
      return;
    }

    return `(${admissions.map((admission) => admission.number).join(', ')})`;
  }

  getIndicativePrice(indicativePrice: number) {
    return indicativePrice / 100;
  }

  findProduct(cropAidId: number): CropAid {
    return this.data.cropAids.find((cropAid) => cropAid.id === cropAidId);
  }

  everyAidAdviceValid() {
    return (
      this.cropAidAdvices.find((cropAidAdvices) => !this.isCropAidValid(cropAidAdvices)) == null
    );
  }

  filteredSalesOrderPeriods(salesOrderPeriods: SalesOrderPeriod[]) {
    return !this.templateType
      ? []
      : salesOrderPeriods.filter((period) => period.periodType === this.templateType);
  }

  confirm($event: Treatment) {
    $event.aidAdvices.forEach((aidAdvice) => {
      aidAdvice.dosage = Number(this.stripOtherThanNumbers(String(aidAdvice.dosage)));
      aidAdvice.forbiddenForPlanetProof = this.findProduct(
        aidAdvice.cropAidId,
      )?.forbiddenForPlanetProof;
    });

    if (!$event.aidAdvices.length || !this.everyAidAdviceValid()) {
      return;
    }

    super.confirm($event);
  }

  getDefaultDriftReduction() {
    return this.hasTemplateTypeGbm() ? 75 : null;
  }

  private filterProducts(value: string): CropAid[] {
    const filterValue = value.toLowerCase();
    return this.data.cropAids
      .filter(
        (option) =>
          option.name.toLowerCase().includes(filterValue) &&
          option.isActive &&
          this.isGbmCropAid(option),
      )
      .sort((a, b) => (a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1));
  }

  newAidAdviceFormGroup() {
    const formGroup = this.formBuilder.group({
      cropAidId: [null, [Validators.pattern(/^\d+$/), Validators.required]],
      dosage: [
        null,
        [
          Validators.required,
          Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/),
          Validators.min(0.001),
          Validators.max(10000000),
        ],
      ],
    });

    formGroup.controls.cropAidId.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(''),
        filter((value) => typeof value === 'string'),
      )
      .subscribe((value: string) => {
        this.filteredProducts$ = of(this.filterProducts(value));
      });

    return formGroup;
  }

  buildAidAdviceFormGroup(aidAdvice: AidAdvice) {
    const formGroup = this.formBuilder.group({
      cropAidId: [aidAdvice.cropAidId, [Validators.pattern(/^\d+$/), Validators.required]],
      dosage: [
        aidAdvice.dosage,
        [
          Validators.required,
          Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/),
          Validators.min(0.001),
          Validators.max(10000000),
        ],
      ],
    });

    formGroup.controls.cropAidId.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(''),
        filter((value) => typeof value === 'string'),
      )
      .subscribe((value: string) => {
        this.filteredProducts$ = of(this.filterProducts(value));
      });

    return formGroup;
  }

  stripOtherThanNumbers(value: string): string {
    if (!value && value !== '0') {
      return '';
    }

    let validCharacters = '.,1234567890';
    let newValue = '';

    for (const character of value) {
      if (validCharacters.includes(character)) {
        newValue += character;
      }
      if (['.', ','].includes(character)) {
        validCharacters = validCharacters.substring(2);
      }
      if (
        (newValue.includes('.') && newValue.length - newValue.indexOf('.') > 4) ||
        (newValue.includes(',') && newValue.length - newValue.indexOf(',') > 4)
      ) {
        newValue = newValue.slice(0, -1);
      }
    }

    return newValue;
  }

  trackByFn(index: number) {
    return index;
  }

  isCropAidValid = (cropAid: CropAid): boolean => this.getCropAidInvalidMessage(cropAid) === null;

  getCropAidInvalidMessage = (cropAid: CropAid): string => {
    if (!cropAid) {
      return null;
    }
    if (cropAid.isActive === false) {
      return this.locale.translate('cropAidInactive');
    }
    if (this.data.isPlanetProof && cropAid.forbiddenForPlanetProof) {
      return this.locale.translate('cropAidForbiddenInPlanetProof');
    }
    return null;
  };
}
